/**
 * External dependencies
 */
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@art-of-hospitality-solutions-llc/ui-kit/components/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@art-of-hospitality-solutions-llc/ui-kit/components/table';
import { useMemo } from 'react';
import {
  Bar,
  BarChart,
  CartesianGrid,
  ComposedChart,
  Legend,
  Line,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';

/**
 * Internal dependencies
 */
import { Card, CardContent } from '@Components/Card';
import Loader from '@Components/Loader';
import { useFirstTouchStats } from '@Hooks/useFirstTouchStats';
import { useFlows } from '@Hooks/useFlows';
import type {
  FirstTouchGroup,
  FirstTouchStats,
  StatsRange,
} from '@Types/messagingStats';
import {
  AXIS_PROPS,
  COLORS,
  ChartCard,
  StatCard,
  formatAgo,
  formatCount,
  formatDay,
  formatDuration,
  formatShare,
} from './shared';

const FIRST_TOUCH_COLORS = {
  touched: COLORS.received,
  waiting: COLORS.recipients,
  missed: COLORS.afterHours,
  instances: COLORS.sent,
  median: COLORS.flow,
};

const touchedDetail = (group: FirstTouchGroup) =>
  `${formatCount(group.touched)} of ${formatCount(group.instances)} touched`;

function Summary({ data }: { data: FirstTouchStats }) {
  return (
    <div className="grid grid-cols-2 gap-4 lg:grid-cols-4">
      <StatCard
        label="Instances started"
        value={formatCount(data.instances)}
        detail={data.flow.title}
      />
      <StatCard
        label="Touched"
        value={formatCount(data.touched)}
        detail={`${formatShare(
          data.instances ? data.touched / data.instances : null,
        )} within ${data.touchWindowDays} days`}
      />
      <StatCard
        label="Median time to first touch"
        value={formatDuration(data.medianSeconds)}
        detail={`90% within ${formatDuration(data.p90Seconds)}`}
      />
      <StatCard
        label="Waiting for a touch"
        value={formatCount(data.waiting)}
        detail={
          data.oldestWaitingStartedAt
            ? `oldest started ${formatAgo(data.oldestWaitingStartedAt)}`
            : `${formatCount(data.missed)} missed (no touch in ${data.touchWindowDays} days)`
        }
      />
      {data.targets.map((target) => (
        <StatCard
          key={target.label}
          label={`Touched within ${target.label}`}
          value={formatShare(target.share)}
          detail={`${formatCount(target.count)} of ${formatCount(data.instances)}`}
        />
      ))}
      <StatCard
        label="Missed"
        value={formatCount(data.missed)}
        detail={`no touch within ${data.touchWindowDays} days`}
      />
      <StatCard
        label="Started in business hours"
        value={formatDuration(data.startedInBusinessHours.medianSeconds)}
        detail={`median · ${touchedDetail(data.startedInBusinessHours)}`}
      />
      <StatCard
        label="Started after hours"
        value={formatDuration(data.startedAfterHours.medianSeconds)}
        detail={`median · ${touchedDetail(data.startedAfterHours)}`}
      />
    </div>
  );
}

function Charts({ data }: { data: FirstTouchStats }) {
  const distribution = [
    ...data.buckets.map((bucket) => ({
      label: bucket.label,
      touched: bucket.count,
    })),
    { label: 'Waiting', waiting: data.waiting },
    { label: 'Missed', missed: data.missed },
  ];
  const grid = (
    <CartesianGrid strokeDasharray="3 3" stroke={COLORS.grid} vertical={false} />
  );

  return (
    <div className="grid gap-4 lg:grid-cols-2">
      <ChartCard title="Time to First Touch">
        <ResponsiveContainer width="100%" height={280}>
          <BarChart data={distribution}>
            {grid}
            <XAxis dataKey="label" interval={0} {...AXIS_PROPS} />
            <YAxis allowDecimals={false} width={40} {...AXIS_PROPS} />
            <Tooltip cursor={{ fill: COLORS.grid }} />
            <Bar
              dataKey="touched"
              name="Touched"
              stackId="touch"
              fill={FIRST_TOUCH_COLORS.touched}
            />
            <Bar
              dataKey="waiting"
              name="Waiting"
              stackId="touch"
              fill={FIRST_TOUCH_COLORS.waiting}
            />
            <Bar
              dataKey="missed"
              name="Missed"
              stackId="touch"
              fill={FIRST_TOUCH_COLORS.missed}
            />
          </BarChart>
        </ResponsiveContainer>
      </ChartCard>
      <ChartCard title="First Touches by Start Day">
        <ResponsiveContainer width="100%" height={280}>
          <ComposedChart data={data.daily}>
            {grid}
            <XAxis
              dataKey="date"
              tickFormatter={(date: string) => formatDay(date, 'MMM d')}
              minTickGap={24}
              {...AXIS_PROPS}
            />
            <YAxis
              yAxisId="count"
              allowDecimals={false}
              width={40}
              {...AXIS_PROPS}
            />
            <YAxis
              yAxisId="median"
              orientation="right"
              tickFormatter={(seconds: number) => formatDuration(seconds)}
              width={56}
              {...AXIS_PROPS}
            />
            <Tooltip
              cursor={{ fill: COLORS.grid }}
              labelFormatter={(date) =>
                formatDay(String(date), 'EEE, MMM d, yyyy')
              }
              formatter={(value, name) =>
                name === 'Median time to first touch'
                  ? [formatDuration(value as number), name]
                  : [value, name]
              }
            />
            <Legend />
            <Bar
              yAxisId="count"
              dataKey="instances"
              name="Started"
              fill={FIRST_TOUCH_COLORS.instances}
            />
            <Bar
              yAxisId="count"
              dataKey="touched"
              name="Touched"
              fill={FIRST_TOUCH_COLORS.touched}
            />
            <Line
              yAxisId="median"
              dataKey="medianSeconds"
              name="Median time to first touch"
              stroke={FIRST_TOUCH_COLORS.median}
              strokeWidth={2}
              dot={false}
            />
          </ComposedChart>
        </ResponsiveContainer>
      </ChartCard>
    </div>
  );
}

function FirstTouchers({ data }: { data: FirstTouchStats }) {
  return (
    <Card>
      <CardContent>
        <div className="mb-2 text-sm font-medium">First Touches by Staff Member</div>
        {!data.users.length ? (
          <div className="py-8 text-center text-sm text-zinc-500">
            No first touches in this window.
          </div>
        ) : (
          <Table className="w-full">
            <TableHeader>
              <TableRow>
                <TableHead>Staff user</TableHead>
                <TableHead className="text-right">First touches</TableHead>
                <TableHead className="text-right">Median time</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {data.users.map((user) => (
                <TableRow key={user.userId}>
                  <TableCell>{user.name}</TableCell>
                  <TableCell className="text-right">
                    {formatCount(user.firstTouches)}
                  </TableCell>
                  <TableCell className="text-right">
                    {formatDuration(user.medianSeconds)}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </CardContent>
    </Card>
  );
}

export default function FirstTouchSection({
  range,
  flowId,
  onFlowChange,
}: {
  range: StatsRange;
  flowId: string | null;
  onFlowChange: (flowId: string) => void;
}) {
  const { data: flows } = useFlows();
  const { data, status, error } = useFirstTouchStats(flowId, range);
  const sortedFlows = useMemo(
    () =>
      [...(flows ?? [])].sort((a, b) => a.flowTitle.localeCompare(b.flowTitle)),
    [flows],
  );

  return (
    <div className="flex flex-col gap-4">
      <div className="flex items-start justify-between gap-4">
        <p className="text-sm text-zinc-500">
          From the moment a fish completes the flow's first stage to the first
          SMS a staff member sends them. The flow's own messages don't count.
        </p>
        <Select value={flowId ?? undefined} onValueChange={onFlowChange}>
          <SelectTrigger className="w-64">
            <SelectValue placeholder="Select a flow" />
          </SelectTrigger>
          <SelectContent>
            {sortedFlows.map((flow) => (
              <SelectItem key={flow.id} value={flow.id}>
                {flow.flowTitle}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>
      {!flowId ? (
        <Card>
          <CardContent>
            <div className="py-8 text-center text-sm text-zinc-500">
              Select a flow to see its time to first touch.
            </div>
          </CardContent>
        </Card>
      ) : status === 'pending' ? (
        <Loader size="lg" />
      ) : status === 'error' ? (
        <Card>
          <CardContent>
            <div className="py-8 text-center text-sm text-red-500">
              {error.message}
            </div>
          </CardContent>
        </Card>
      ) : (
        <>
          <Summary data={data} />
          <Charts data={data} />
          <FirstTouchers data={data} />
        </>
      )}
    </div>
  );
}
